import React, { useRef, useEffect, useState } from 'react';
import formStyles from '../../styles/SmartSendForm.module.css';

/**
 * ReVerificationStep - Handles phone re-verification during submission
 * Displayed when backend returns PHONE_VERIFICATION_REQUIRED (code expired)
 * Allows user to enter verification code and request a retry
 *
 * Features:
 * - 6-digit code input with auto-advance and auto-submit
 * - Paste handling for mobile keyboards (SMS autofill)
 * - Input clearing on verification failure
 * - Resend code functionality with cooldown
 * - Follows SmartSend v2 conventions (CSS modules, accessibility)
 *
 * @param {string} message - Message from backend to display to user (already contains phone number)
 * @param {Function} onVerify - Handler for verifying entered code
 * @param {Function} onResendCode - Handler for requesting a new code
 * @param {boolean} isVerifying - Loading state during code verification
 * @param {boolean} isResending - Loading state during code resend
 */
const CODE_LENGTH = 6;
const AUTO_SUBMIT_DELAY = 100;

const ReVerificationStep = ({
  message = 'We need to verify your phone number.',
  onVerify,
  onResendCode,
  isVerifying = false,
  isResending = false,
}) => {
  const inputRefs = useRef([]);
  const [error, setError] = useState('');
  const prevVerifyingRef = useRef(isVerifying);

  useEffect(() => {
    // Auto-focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Clear inputs on verification failure
  useEffect(() => {
    // Detect when verification completes unsuccessfully
    // (isVerifying changed from true to false, but verification failed)
    if (prevVerifyingRef.current === true && isVerifying === false) {
      // Clear all input fields
      inputRefs.current.forEach((input) => {
        if (input) {
          input.value = '';
        }
      });

      // Focus first input for immediate retry
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    }

    // Update ref for next comparison
    prevVerifyingRef.current = isVerifying;
  }, [isVerifying]);

  /**
   * Gets the current code from all input fields
   * @returns {string} The 6-digit code or empty string
   */
  const getCurrentCode = () => {
    return inputRefs.current.map((input) => input?.value || '').join('');
  };

  /**
   * Checks if all inputs are filled and auto-submits if ready
   */
  const checkAndSubmitCode = () => {
    const code = getCurrentCode();
    if (code.length === CODE_LENGTH && !isVerifying) {
      setTimeout(() => {
        onVerify(code);
      }, AUTO_SUBMIT_DELAY);
    }
  };

  /**
   * Fills input fields with digits starting from the specified index
   * @param {number} startIndex - Index to start filling from
   * @param {string} digits - String of digits to fill
   */
  const fillInputsWithDigits = (startIndex, digits) => {
    digits.split('').forEach((digit, i) => {
      if (inputRefs.current[startIndex + i]) {
        inputRefs.current[startIndex + i].value = digit;
      }
    });

    // Focus the next empty input or the last input if all filled
    const nextIndex = Math.min(startIndex + digits.length, CODE_LENGTH - 1);
    if (inputRefs.current[nextIndex]) {
      inputRefs.current[nextIndex].focus();
    }
  };

  const handleInputChange = (index, value) => {
    // Clear error when user types
    if (error) {
      setError('');
    }

    // Handle autofill scenarios where multiple digits might be entered at once
    const numericValue = value.replace(/\D/g, '');

    // If multiple digits detected (autofill scenario), distribute them
    if (numericValue.length > 1) {
      const digits = numericValue.slice(0, CODE_LENGTH);
      fillInputsWithDigits(index, digits);
      checkAndSubmitCode();
      return;
    }

    // Single digit input (normal typing)
    const singleDigit = numericValue.slice(0, 1);
    if (singleDigit && inputRefs.current[index]) {
      inputRefs.current[index].value = singleDigit;

      // Auto-advance to next input
      if (index < CODE_LENGTH - 1 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }

      checkAndSubmitCode();
    }
  };

  /**
   * Handles paste events and Chrome SMS autofill
   * Distributes pasted/autofilled code across all input fields starting from the first
   * @param {ClipboardEvent} e - Paste event
   */
  const handlePaste = (e) => {
    e.preventDefault();

    // Safely get clipboard data with fallback
    const clipboardData = e.clipboardData || window.clipboardData;
    if (!clipboardData) {
      return;
    }

    const pastedData = clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);

    if (pastedData.length > 0) {
      // Always start from the first input when pasting
      fillInputsWithDigits(0, pastedData);
      checkAndSubmitCode();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace to go to previous input
    if (
      e.key === 'Backspace' &&
      !inputRefs.current[index]?.value &&
      index > 0
    ) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /**
   * Handle verify button click (manual submit)
   */
  const handleVerify = () => {
    const code = getCurrentCode();
    if (code.length !== CODE_LENGTH) {
      setError('Please enter a 6-digit code');
      return;
    }
    onVerify(code);
  };

  /**
   * Handle resend code click
   */
  const handleResend = () => {
    // Clear all inputs
    inputRefs.current.forEach((input) => {
      if (input) {
        input.value = '';
      }
    });
    setError('');
    onResendCode();
  };

  return (
    <div
      className={`${formStyles.card} ${formStyles.missingInfoCard}`}
      role="form"
      aria-label="Phone re-verification form"
    >
      <h3
        className={`${formStyles.sectionTitle} ${formStyles.missingInfoTitle}`}
        id="reverify-title"
      >
        Phone Verification Required
      </h3>

      <p className={formStyles.infoTextSecondary} id="reverify-message">
        {message}
      </p>

      <label className={formStyles.label} id="reverify-code-label">
        Enter Code
      </label>
      <div
        className={formStyles.otp}
        role="group"
        aria-labelledby="reverify-code-label"
        aria-describedby={error ? 'reverify-error' : undefined}
      >
        {Array.from({ length: CODE_LENGTH }, (_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            inputMode="numeric"
            autoComplete="one-time-code"
            className={formStyles.otpInput}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={isVerifying || isResending}
            aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
            aria-required="true"
            aria-invalid={!!error}
            type="text"
            pattern="[0-9]"
          />
        ))}
      </div>
      {error && (
        <p className={formStyles.errorText} id="reverify-error" role="alert">
          {error}
        </p>
      )}

      <button
        className={`${formStyles.button} ${formStyles.verifyButton}`}
        onClick={handleVerify}
        disabled={
          getCurrentCode().length !== CODE_LENGTH || isVerifying || isResending
        }
        aria-label={isVerifying ? 'Verifying code' : 'Verify code and continue'}
        aria-busy={isVerifying}
      >
        {isVerifying ? 'Verifying...' : 'Verify Code'}
      </button>

      <div className={formStyles.resendContainer}>
        <button
          type="button"
          onClick={handleResend}
          disabled={isVerifying || isResending}
          className={formStyles.resendButton}
          aria-label="Resend verification code to your phone"
          aria-busy={isResending}
        >
          {isResending ? 'Sending...' : "Didn't receive a code? Resend"}
        </button>
      </div>
    </div>
  );
};

export default ReVerificationStep;
